import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, DollarSign, CheckCircle, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCovers = () => {
  const navigate = useNavigate();
  const [covers, setCovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCover, setSelectedCover] = useState(null);
  const [showRepaymentDialog, setShowRepaymentDialog] = useState(false);
  const [repaymentData, setRepaymentData] = useState({ amount: "", note: "" });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadCovers();
  }, []);

  const loadCovers = async () => {
    try {
      const response = await axios.get(`${API}/admin/covers`, {
        withCredentials: true
      });
      setCovers(response.data);
    } catch (error) {
      toast.error('Failed to load covers');
    } finally {
      setLoading(false);
    }
  };

  const handleLogRepayment = async () => {
    if (!repaymentData.amount) {
      toast.error('Please enter an amount');
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/cover/${selectedCover.cover_id}/repayment`,
        { 
          amount: parseFloat(repaymentData.amount),
          note: repaymentData.note
        },
        { withCredentials: true }
      );
      
      if (response.data.status === 'success') {
        toast.success('Repayment charged successfully!');
      } else {
        toast.error(`Charge failed: ${response.data.error_message}`);
      }
      
      setShowRepaymentDialog(false);
      setRepaymentData({ amount: "", note: "" });
      loadCovers();
    } catch (error) {
      toast.error('Failed to log repayment');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusColors = {
    active: "bg-blue-100 text-blue-800",
    paid: "bg-green-100 text-green-800",
    defaulted: "bg-red-100 text-red-800"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/10 to-background py-12 px-6">
      <div className="container mx-auto max-w-7xl">
        <Button 
          data-testid="back-to-admin-btn"
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Admin
        </Button>

        <div className="bg-card rounded-3xl shadow-soft p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold mb-2" data-testid="covers-title">
                Covers & Repayments
              </h1>
              <p className="text-muted-foreground">{covers.length} total covers</p>
            </div>
          </div>

          {covers.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No covers yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {covers.map((cover) => {
                const remaining = cover.amount_approved - cover.total_repaid;
                const percentPaid = (cover.total_repaid / cover.amount_approved) * 100;

                return (
                  <div 
                    key={cover.cover_id}
                    data-testid={`cover-${cover.cover_id}`}
                    className="p-6 border-2 border-border rounded-2xl hover:shadow-soft transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-heading font-bold">
                            {cover.user?.name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[cover.status]}`}>
                            {cover.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {cover.user?.email} - Tier: <span className="capitalize">{cover.user?.membership_tier}</span>
                        </p>
                        <div className="bg-muted/30 p-4 rounded-xl mb-3">
                          <p className="text-sm font-medium mb-2">Repayment Plan:</p>
                          <p className="text-sm text-muted-foreground">{cover.repayment_plan_text}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Created: {new Date(cover.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right ml-6">
                        <div className="mb-4">
                          <p className="text-sm text-muted-foreground mb-1">Amount Approved</p>
                          <p className="text-3xl font-heading font-bold text-primary">
                            ${cover.amount_approved.toFixed(2)}
                          </p>
                        </div>
                        <div className="mb-4">
                          <p className="text-sm text-muted-foreground mb-1">Total Repaid</p>
                          <p className="text-2xl font-heading font-semibold text-green-600">
                            ${cover.total_repaid.toFixed(2)}
                          </p>
                        </div>
                        <div className="mb-4">
                          <p className="text-sm text-muted-foreground mb-1">Remaining</p>
                          <p className="text-xl font-heading font-semibold text-amber-600">
                            ${remaining.toFixed(2)}
                          </p>
                        </div>
                        {cover.status === 'active' && (
                          <Button
                            data-testid={`log-repayment-${cover.cover_id}-btn`}
                            onClick={() => {
                              setSelectedCover(cover);
                              setRepaymentData({ amount: remaining.toFixed(2), note: "" });
                              setShowRepaymentDialog(true);
                            }}
                            className="btn-primary w-full mt-2"
                          >
                            Log Repayment
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Repayment Progress</span>
                        <span className="font-semibold">{percentPaid.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div 
                          className="bg-green-600 rounded-full h-3 transition-all duration-300"
                          style={{ width: `${Math.min(percentPaid, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Repayment History */}
                    {cover.repayments && cover.repayments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <h4 className="text-lg font-heading font-semibold mb-3">Repayment History</h4>
                        <div className="space-y-2">
                          {cover.repayments.map((repayment) => (
                            <div 
                              key={repayment.repayment_id}
                              data-testid={`repayment-${repayment.repayment_id}`}
                              className="flex items-center justify-between p-3 bg-muted/20 rounded-lg text-sm"
                            >
                              <div className="flex items-center gap-3">
                                {repayment.status === 'success' ? (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600" />
                                )}
                                <div>
                                  <p className="font-medium">${repayment.amount.toFixed(2)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(repayment.date).toLocaleDateString()}
                                  </p>
                                  {repayment.note && (
                                    <p className="text-xs text-muted-foreground mt-1">{repayment.note}</p>
                                  )}
                                  {repayment.error_message && (
                                    <p className="text-xs text-red-600 mt-1">{repayment.error_message}</p>
                                  )}
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                repayment.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {repayment.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Repayment Dialog */}
      <Dialog open={showRepaymentDialog} onOpenChange={setShowRepaymentDialog}>
        <DialogContent data-testid="repayment-dialog">
          <DialogHeader>
            <DialogTitle>Log Repayment</DialogTitle>
            <DialogDescription>
              Charge the member's card for a repayment. This will use Stripe to create a payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="repayment-amount" data-testid="repayment-amount-label">Amount to Charge</Label>
              <Input
                id="repayment-amount"
                data-testid="repayment-amount-input"
                type="number"
                step="0.01"
                placeholder="30.00"
                value={repaymentData.amount}
                onChange={(e) => setRepaymentData({...repaymentData, amount: e.target.value})}
                className="input-default mt-2"
              />
            </div>
            <div>
              <Label htmlFor="repayment-note" data-testid="repayment-note-label">Note (Optional)</Label>
              <Textarea
                id="repayment-note"
                data-testid="repayment-note-textarea"
                placeholder="e.g., Weekly payment 1 of 5"
                value={repaymentData.note}
                onChange={(e) => setRepaymentData({...repaymentData, note: e.target.value})}
                className="input-default mt-2"
              />
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                This will charge the member's default payment method via Stripe. Make sure the amount is correct.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="confirm-repayment-btn"
                onClick={handleLogRepayment}
                disabled={actionLoading}
                className="btn-primary flex-1"
              >
                {actionLoading ? 'Processing...' : 'Charge & Log Payment'}
              </Button>
              <Button
                data-testid="cancel-repayment-btn"
                onClick={() => setShowRepaymentDialog(false)}
                variant="outline"
                className="btn-outline flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCovers;
